import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { SUPPORT_EMAIL } from "@/config/support";

// Full-screen recovery experience shown when any API call returns
// 403 ACCOUNT_DISABLED (dispatched by the shared axios interceptor).
export const AccountDisabledGate = () => {
  const [disabled, setDisabled] = useState(false);
  const [suspended, setSuspended] = useState(false);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onDisabled = async (e) => {
      setDisabled(true);
      if (e?.detail?.code === "ACCOUNT_SUSPENDED") setSuspended(true);
      try { setInfo((await api.get("/account/status")).data); } catch (_) { /* keep defaults */ }
    };
    window.addEventListener("advoset:account-disabled", onDisabled);
    return () => window.removeEventListener("advoset:account-disabled", onDisabled);
  }, []);

  if (!disabled) return null;

  const reenable = async () => {
    setBusy(true);
    try {
      await api.post("/account/reenable", {});
      toast.success("✓ Account re-enabled");
      setTimeout(() => window.location.reload(), 700);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not re-enable your account");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-[#0A2540]/95 backdrop-blur-sm flex items-center justify-center p-6"
         data-testid="account-disabled-screen">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl">
        <div className="flex items-center gap-2 text-amber-600 font-extrabold text-lg mb-3"
             style={{ fontFamily: "Poppins" }}>
          <AlertTriangle size={20} /> {suspended ? "Account Suspended" : "Account Disabled"}
        </div>
        <p className="text-sm text-slate-600 mb-1" data-testid="account-disabled-message">
          {info?.name ? `Hi ${info.name}, your` : "Your"} Advoset account is currently {suspended ? "suspended by an administrator" : "disabled"}.
        </p>
        <p className="text-sm text-slate-600 mb-6">
          {suspended
            ? "You cannot use Advoset services while your account is suspended. Only an administrator can restore access."
            : "You cannot use Advoset services while your account is disabled. Re-enable your account to continue using Advoset."}
        </p>
        {!suspended && (
          <Button onClick={reenable} disabled={busy} className="w-full adv-btn-primary font-bold rounded-xl"
                  data-testid="account-reenable-btn">
            {busy ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
            Re-enable Account
          </Button>
        )}
        <p className="text-[11px] text-slate-400 mt-4 text-center">
          Need help? Contact{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="underline hover:text-slate-600">
            {SUPPORT_EMAIL}
          </a>
        </p>
      </div>
    </div>
  );
};
